import {
  Modal,
  Box,
  Typography,
  IconButton,
  Stack,
  Grid,
  Button,
} from "@mui/material";
import CustomTextField from "../../../../../components/common/Textfield";
import { useEffect, useState } from "react";
import { IComment } from "../../../../../types/comment";
import CloseIcon from "@mui/icons-material/Close";
import PhoneNumberField from "../../../../../components/common/PhoneNumberField";
import apiService from "../../../../../service/Api";
import {
  showErrorToast,
  showSuccessToast,
} from "../../../../../components/common/ShowToast";
import { useSelector } from "react-redux";
import type { RootState } from "../../../../..//store/Index";
import PropTypes from "prop-types";

interface AddContactModalProps {
  comment: IComment;
  setAddContactModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
}

export const AddContactModal: React.FC<AddContactModalProps> = ({
  comment,
  setAddContactModalOpen,
}) => {
  const reduxTheme = useSelector((state: RootState) => state.theme);
  const [email, setEmail] = useState<string>(comment.authorEmail || "");
  const [emailError, setEmailError] = useState<string>("");
  const [firstName, setFirstName] = useState<string>(
    comment.author.split(" ")[0] || ""
  );
  const [lastName, setLastName] = useState<string>("");
  const [phoneNumber, setPhoneNumber] = useState<string>(comment.authorPhone || "");

  useEffect(() => {
    getLastname(comment.author);
  }, [comment.author]);

  const validateEmail = (email: string): string => {
    if (!email) return "";
    const regex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    if (!regex.test(email)) return "Invalid email format.";
    if (email.startsWith(".")) return "Email cannot start with a dot.";
    if (email.endsWith(".")) return "Email cannot end with a dot.";
    if (email.split("@")[0].endsWith("."))
      return "Local part cannot end with a dot.";
    return "";
  };

  const getLastname = (name: string) => {
    const splittedName = name.split(" ");

    if (splittedName.length > 1) {
      setLastName(splittedName.at(-1) ?? "");
    }
  };

  const handleEmailChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const error = validateEmail(e.target.value);
    setEmailError(error);
    setEmail(e.target.value);
  };

  const handleSubmit = async () => {
    if (!firstName || !lastName) {
      showErrorToast("First name and last name are required.");
      return;
    }

    if (!email && !phoneNumber) {
      showErrorToast(
        "Provide at least one contact method (email or phone number)."
      );
      return;
    }

    if (emailError && email) {
      showErrorToast("Please provide a valid email address");
      return;
    }

    try {
      // Check if a contact with this email already exists
      if (email) {
        const searchResponse = await apiService.get<{
          count: number;
          results: Array<{
            id: number;
            first_name: string | null;
            last_name: string | null;
            phone: string | null;
            email: string | null;
          }>;
        }>(`/api/contacts/contact/?email=${encodeURIComponent(email)}`);

        if (searchResponse.data.count > 0) {
          const existing = searchResponse.data.results[0];
          // Update missing fields on the existing contact
          const updates: Record<string, string> = {};
          if (!existing.first_name && firstName) updates.first_name = firstName;
          if (!existing.last_name && lastName) updates.last_name = lastName;
          if (
            (!existing.phone || existing.phone === "N/A") &&
            phoneNumber
          )
            updates.phone = phoneNumber;

          if (Object.keys(updates).length > 0) {
            await apiService.patch(
              `/api/contacts/contact/${existing.id}/`,
              updates
            );
            showSuccessToast(
              `Contact "${existing.first_name || firstName} ${existing.last_name || lastName}" already exists. Updated missing fields: ${Object.keys(updates).join(", ")}.`
            );
          } else {
            showSuccessToast(
              `Contact "${existing.first_name} ${existing.last_name}" already exists with this email. No fields needed updating.`
            );
          }
          setAddContactModalOpen(false);
          return;
        }
      }

      // No existing contact found — create new
      const formData = new FormData();
      formData.append("contact_type", "individual");
      formData.append("first_name", firstName);
      formData.append("last_name", lastName);
      if (email) {
        formData.append("email", email);
      }
      if (phoneNumber) {
        formData.append("phone", phoneNumber);
      }

      await apiService.post<unknown>("/api/contacts/contact/", formData, false);
      showSuccessToast("Individual contact added successfully.");
      setAddContactModalOpen(false);
    } catch (err: unknown) {
      console.error("Failed to add contact:", err);
      showErrorToast("Failed to add contact, please try again");
    }
  };

  return (
    <Modal
      open={true}
      onClose={() => setAddContactModalOpen(false)}
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description"
    >
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: 600,
          bgcolor: "background.paper",
          border: "2px solid #fff",
          boxShadow: 24,
          borderRadius: 1,
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: ".8em 2em",
            borderBottom: `1px solid ${reduxTheme.primary_colors[300]}`,
          }}
        >
          <Typography id="modal-modal-title" variant="h6" component="h2">
            Add Contact
          </Typography>

          <IconButton sx={{ "&:hover": { color: "#a60b0bff" } }}>
            <CloseIcon onClick={() => setAddContactModalOpen(false)} />
          </IconButton>
        </Box>

        <Stack spacing={2} sx={{ padding: "0.8em 2em", mt: 2 }}>
          <Box
            sx={{ display: "flex", justifyContent: "space-between", gap: 2 }}
          >
            <CustomTextField
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              fullWidth
              label="Firsname"
              variant="outlined"
              required
            />

            <CustomTextField
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              fullWidth
              label="Surname"
              variant="outlined"
              required
            />
          </Box>
          <CustomTextField
            value={email}
            onChange={(e) => handleEmailChange(e)}
            label="Email"
            variant="outlined"
          />
          <PhoneNumberField
            fullWidth
            label="Phone Number"
            value={phoneNumber}
            onChange={(e) => setPhoneNumber(e)}
            defaultCountry="ZW"
            variant="outlined"
            sx={{ marginBottom: "15px" }}
          />
        </Stack>

        <Grid
          container
          spacing={1}
          justifyContent={"end"}
          sx={{ mt: 2, paddingInline: "2em", mb: 3 }}
        >
          <Grid>
            <Button
              variant="outlined"
              onClick={() => setAddContactModalOpen(false)}
            >
              Cancel
            </Button>
          </Grid>

          <Grid>
            <Button variant="contained" onClick={handleSubmit}>
              Add Contact
            </Button>
          </Grid>
        </Grid>
      </Box>
    </Modal>
  );
};

AddContactModal.propTypes = {
  comment: PropTypes.shape({
    author: PropTypes.string.isRequired,
    authorEmail: PropTypes.string,
  }).isRequired,
  setAddContactModalOpen: PropTypes.func.isRequired,
};
